import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS  = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// ─── helpers ────────────────────────────────────────────────────────────────
function padZ(n) { return String(n).padStart(2, '0'); }
function ymd(d)  { return `${d.getFullYear()}-${padZ(d.getMonth()+1)}-${padZ(d.getDate())}`; }
function fmtHora(h) {
  const [hh, mm] = h.split(':');
  const hr = parseInt(hh);
  return `${padZ(hr > 12 ? hr - 12 : hr || 12)}:${mm} ${hr < 12 ? 'AM' : 'PM'}`;
}
function fmtFechaLarga(ymdStr) {
  const [y,m,d] = ymdStr.split('-').map(Number);
  const fecha = new Date(y, m-1, d);
  return fecha.toLocaleDateString('es-EC', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
}
function fmtDuracion(min) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min/60), m = min%60;
  return m ? `${h}h ${m}min` : `${h}h`;
}

// ─── Componente ─────────────────────────────────────────────────────────────
export default function ReservaPublica() {
  const { codigo } = useParams();
  const [searchParams] = useSearchParams();

  const [negocio,       setNegocio]       = useState(null);
  const [errorGlobal,   setErrorGlobal]   = useState('');
  const [step,          setStep]          = useState(1); // 1-4, 5=confirmado, 6=verificando PP
  const [servicios,     setServicios]     = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [slots,         setSlots]         = useState([]);
  const [loadSlots,     setLoadSlots]     = useState(false);
  const [diaCerrado,    setDiaCerrado]    = useState(false);

  // selecciones
  const [servicio,     setServicio]     = useState(null);
  const [profesional,  setProfesional]  = useState(null); // null = sin preferencia
  const [fecha,        setFecha]        = useState('');
  const [hora,         setHora]         = useState('');
  const [form,         setForm]         = useState({ nombre:'', apellido:'', whatsapp:'', cedula:'', email:'', notas:'' });
  const [enviando,     setEnviando]     = useState(false);
  const [errorEnvio,   setErrorEnvio]   = useState('');
  const [confirmacion, setConfirmacion] = useState(null);

  // búsqueda por cédula
  const [cedulaBusqueda,   setCedulaBusqueda]   = useState('');
  const [buscandoCliente,  setBuscandoCliente]  = useState(false);
  const [clienteEncontrado,setClienteEncontrado]= useState(null);  // object | false
  const [clienteBuscado,   setClienteBuscado]   = useState(false);
  const [modalNuevoCliente,setModalNuevoCliente]= useState(false);

  // comprobante
  const [numeroComprobante,    setNumeroComprobante]    = useState('');
  const [comprobanteFile,      setComprobanteFile]      = useState(null);
  const [comprobantePreview,   setComprobantePreview]   = useState('');
  const [guardandoComprobante, setGuardandoComprobante] = useState(false);
  const [comprobanteGuardado,  setComprobanteGuardado]  = useState(false);

  // payphone
  const [ppVerificando, setPpVerificando] = useState(false);
  const [ppError,       setPpError]       = useState('');
  const [enviandoPP,    setEnviandoPP]    = useState(false);

  // lista de espera
  const [listaEsperaMode,   setListaEsperaMode]   = useState(false);
  const [formEspera,        setFormEspera]        = useState({ nombre:'', apellido:'', telefono:'', email:'' });
  const [enviandoEspera,    setEnviandoEspera]    = useState(false);
  const [confirmacionEspera,setConfirmacionEspera]= useState(false);
  const [errorEspera,       setErrorEspera]       = useState('');

  // calendario
  const hoy = new Date(); hoy.setHours(0,0,0,0);
  const [calMes,  setCalMes]  = useState(hoy.getMonth());
  const [calAnio, setCalAnio] = useState(hoy.getFullYear());

  // ── cargar negocio ────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/public/${codigo}/info`)
      .then(r => setNegocio(r.data))
      .catch(e => setErrorGlobal(e.response?.data?.error || 'Negocio no encontrado'));
  }, [codigo]);

  // ── cargar servicios ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!negocio) return;
    axios.get(`${API}/public/${codigo}/servicios`)
      .then(r => setServicios(r.data))
      .catch(() => {});
  }, [negocio, codigo]);

  // ── cargar profesionales al elegir servicio ───────────────────────────────
  useEffect(() => {
    if (!servicio) return;
    axios.get(`${API}/public/${codigo}/profesionales?servicio_id=${servicio.id}`)
      .then(r => setProfesionales(r.data))
      .catch(() => {});
  }, [servicio, codigo]);

  // ── cargar slots al elegir fecha ─────────────────────────────────────────
  const fetchSlots = useCallback(() => {
    if (!fecha || !servicio) return;
    setLoadSlots(true);
    setSlots([]);
    setHora('');
    setDiaCerrado(false);
    const params = `fecha=${fecha}&servicio_id=${servicio.id}${profesional ? `&profesional_id=${profesional.id}` : ''}`;
    axios.get(`${API}/public/${codigo}/disponibilidad?${params}`)
      .then(r => {
        const data = r.data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          setSlots(data.slots || []);
          setDiaCerrado(data.cerrado || false);
        } else {
          setSlots(Array.isArray(data) ? data : []);
          setDiaCerrado(false);
        }
      })
      .catch(() => { setSlots([]); setDiaCerrado(false); })
      .finally(() => setLoadSlots(false));
  }, [fecha, servicio, profesional, codigo]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  // ── verificar slot al llegar al paso 4 ───────────────────────────────────
  useEffect(() => {
    if (step !== 4 || !fecha || !hora || !servicio) return;
    const params = `fecha=${fecha}&servicio_id=${servicio.id}${profesional ? `&profesional_id=${profesional.id}` : ''}`;
    axios.get(`${API}/public/${codigo}/disponibilidad?${params}`)
      .then(r => {
        const slotsDisp = Array.isArray(r.data) ? r.data : (r.data.slots || []);
        if (!slotsDisp.includes(hora)) {
          setErrorEnvio('El horario seleccionado ya no está disponible. Por favor elige otro.');
          setHora('');
          setStep(3);
        }
      })
      .catch(() => {});
  }, [step]);

  // ── lista de espera ──────────────────────────────────────────────────────
  const handleSubmitEspera = async () => {
    setErrorEspera('');
    if (!formEspera.nombre.trim() || !formEspera.telefono.trim()) {
      setErrorEspera('Nombre y teléfono son obligatorios');
      return;
    }
    setEnviandoEspera(true);
    try {
      await axios.post(`${API}/public/${codigo}/lista-espera`, {
        nombre:      formEspera.nombre.trim(),
        apellido:    formEspera.apellido.trim(),
        telefono:    formEspera.telefono.trim(),
        email:       formEspera.email.trim(),
        servicio_id: servicio?.id || null,
        fecha,
      });
      setConfirmacionEspera(true);
    } catch (e) {
      setErrorEspera(e.response?.data?.error || 'Error al registrarse. Intenta de nuevo.');
    } finally {
      setEnviandoEspera(false);
    }
  };

  // ── buscar cliente por cédula ─────────────────────────────────────────────
  const buscarCliente = async () => {
    if (!cedulaBusqueda.trim()) return;
    setBuscandoCliente(true);
    try {
      const { data } = await axios.get(`${API}/public/${codigo}/cliente?cedula=${encodeURIComponent(cedulaBusqueda.trim())}`);
      setClienteEncontrado(data);
      setClienteBuscado(true);
    } catch {
      setClienteEncontrado(false);
      setForm(f => ({ ...f, cedula: cedulaBusqueda.trim() }));
      setModalNuevoCliente(true);
    } finally {
      setBuscandoCliente(false);
    }
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleComprobanteFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setComprobanteFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setComprobantePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── enviar reserva ────────────────────────────────────────────────────────
  const handleReservar = async () => {
    setErrorEnvio('');

    const cedula   = clienteEncontrado ? clienteEncontrado.identificacion  : form.cedula.trim();
    const nombre   = clienteEncontrado ? clienteEncontrado.nombres         : form.nombre.trim();
    const whatsapp = clienteEncontrado ? clienteEncontrado.whatsapp_number : form.whatsapp.trim();
    if (!nombre || !cedula) {
      setErrorEnvio('Nombre y Cédula son obligatorios');
      return;
    }
    if (!whatsapp) {
      setErrorEnvio('El número de WhatsApp es obligatorio');
      return;
    }

    // Comprobante obligatorio si el servicio requiere depósito por transferencia
    if (servicio?.requiere_deposito && negocio?.pago?.transferencia) {
      if (!numeroComprobante.trim()) {
        setErrorEnvio('El número de comprobante es obligatorio para completar la reserva');
        return;
      }
      if (!comprobanteFile) {
        setErrorEnvio('Debes adjuntar la imagen del comprobante de pago');
        return;
      }
    }

    setEnviando(true);
    try {
      let comprobanteImagenBase64 = '';
      if (comprobanteFile) {
        comprobanteImagenBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(comprobanteFile);
        });
      }

      const { data } = await axios.post(`${API}/public/${codigo}/reservar`, {
        nombre:             clienteEncontrado ? clienteEncontrado.nombres       : form.nombre.trim(),
        apellido:           clienteEncontrado ? clienteEncontrado.apellidos      : form.apellido.trim(),
        whatsapp:           clienteEncontrado ? clienteEncontrado.whatsapp_number: form.whatsapp.trim(),
        cedula,
        email:              clienteEncontrado ? clienteEncontrado.email          : form.email.trim(),
        notas:              form.notas.trim(),
        numero_comprobante: numeroComprobante.trim(),
        comprobante_imagen: comprobanteImagenBase64,
        servicio_id:        servicio.id,
        profesional_id:     profesional?.id || null,
        fecha,
        hora,
      });
      setConfirmacion({ ...data, numero_comprobante: numeroComprobante.trim() });
      setStep(5);
    } catch (e) {
      setErrorEnvio(e.response?.data?.error || 'Error al confirmar la reserva. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  // ── guardar comprobante y abrir WhatsApp ─────────────────────────────────
  const handleEnviarComprobante = async (waLink) => {
    setGuardandoComprobante(true);
    try {
      let imagenBase64 = '';
      if (comprobanteFile) {
        imagenBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = reject;
          reader.readAsDataURL(comprobanteFile);
        });
      }
      await axios.patch(
        `${API}/public/${codigo}/cita/${confirmacion.cita_id}/comprobante`,
        { numero_comprobante: numeroComprobante.trim(), comprobante_imagen: imagenBase64 }
      );
      setComprobanteGuardado(true);
    } catch (err) {
      console.error('Error guardando comprobante:', err);
    } finally {
      setGuardandoComprobante(false);
      if (waLink) window.open(waLink, '_blank');
    }
  };

  // ── retorno de PayPhone ───────────────────────────────────────────────────
  useEffect(() => {
    const ppCita = searchParams.get('pp_cita');
    const ppCtid = searchParams.get('pp_ctid');
    const ppId   = searchParams.get('id');           // PayPhone append its own id
    const ppCancel = searchParams.get('pp_cancel');

    if (ppCancel) {
      setPpError('Pago cancelado. Tu reserva no fue confirmada.');
      setStep(6);
      return;
    }
    if (ppCita && ppId && negocio) {
      setStep(6);
      setPpVerificando(true);
      axios.post(`${API}/public/${codigo}/pago/payphone/verificar`, {
        pp_id:   ppId,
        pp_cita: ppCita,
        pp_ctid: ppCtid,
      })
        .then(r => {
          if (r.data.confirmada) {
            setConfirmacion({ ...r.data, _pp: true });
            setStep(5);
          } else {
            setPpError(`Pago no aprobado: ${r.data.razon || 'intenta de nuevo'}`);
          }
        })
        .catch(() => setPpError('No se pudo verificar el pago. Contacta al negocio.'))
        .finally(() => setPpVerificando(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocio]);

  // ── pagar con PayPhone ────────────────────────────────────────────────────
  const handlePayPhone = async () => {
    setErrorEnvio('');
    const cedula = clienteEncontrado ? clienteEncontrado.identificacion : form.cedula.trim();
    const nombre = clienteEncontrado ? clienteEncontrado.nombres        : form.nombre.trim();
    if (!nombre || !cedula) {
      setErrorEnvio('Nombre y Cédula son obligatorios');
      return;
    }
    setEnviandoPP(true);
    try {
      const { data } = await axios.post(`${API}/public/${codigo}/pago/payphone`, {
        nombre:    clienteEncontrado ? clienteEncontrado.nombres        : form.nombre.trim(),
        apellido:  clienteEncontrado ? clienteEncontrado.apellidos      : form.apellido.trim(),
        whatsapp:  clienteEncontrado ? clienteEncontrado.whatsapp_number: form.whatsapp.trim(),
        cedula,
        email:     clienteEncontrado ? clienteEncontrado.email          : form.email.trim(),
        notas:     form.notas.trim(),
        servicio_id:   servicio.id,
        fecha,
        hora,
      });
      // Redirigir a PayPhone
      window.location.href = data.url;
    } catch (e) {
      setErrorEnvio(e.response?.data?.error || 'Error al iniciar el pago. Intenta de nuevo.');
      setEnviandoPP(false);
    }
  };

  // ── calendario ────────────────────────────────────────────────────────────
  function diasDelMes() {
    const primero = new Date(calAnio, calMes, 1);
    const ultimo  = new Date(calAnio, calMes + 1, 0);
    const celdas  = [];
    for (let i = 0; i < primero.getDay(); i++) celdas.push(null);
    for (let d = 1; d <= ultimo.getDate(); d++) celdas.push(new Date(calAnio, calMes, d));
    return celdas;
  }
  function mesAnterior() {
    if (calMes === 0) { setCalMes(11); setCalAnio(a => a-1); }
    else setCalMes(m => m-1);
  }
  function mesSiguiente() {
    if (calMes === 11) { setCalMes(0); setCalAnio(a => a+1); }
    else setCalMes(m => m+1);
  }
  function esDisponible(d) {
    if (!d) return false;
    const dp = new Date(d); dp.setHours(0,0,0,0);
    return dp >= hoy;
  }

  // ─────────────────────────────────────────────────────────────────────────
  if (errorGlobal) return (
    <div style={styles.errorPage}>
      <div style={styles.errorBox}>
        <span style={{ fontSize:'3rem' }}>😔</span>
        <h2>Página no disponible</h2>
        <p>{errorGlobal}</p>
      </div>
    </div>
  );

  if (!negocio) return (
    <div style={styles.loadingPage}>
      <div style={styles.spinner} />
      <p style={{ color:'#6b7280', marginTop:'1rem' }}>Cargando...</p>
    </div>
  );

  // ── Paso 5: confirmación ──────────────────────────────────────────────────
  if (step === 5) {
    const esPago = confirmacion?.pendiente_pago;
    const pago   = confirmacion?.pago;
    const nombreCliente = clienteEncontrado
      ? `${clienteEncontrado.nombres} ${clienteEncontrado.apellidos}`
      : `${form.nombre} ${form.apellido}`;
    const waMensaje = pago?.whatsapp_cobros
      ? encodeURIComponent(
          `Hola! Te envío mi comprobante de pago para mi reserva.\n` +
          `Servicio: ${servicio.nombre}\nFecha: ${fmtFechaLarga(fecha)} a las ${fmtHora(hora)}\n` +
          `Nombre: ${nombreCliente}\nMonto: $${pago.monto?.toFixed(2)}\n` +
          `N° Comprobante: ${numeroComprobante.trim() || '(pendiente)'}`
        )
      : null;
    const waNum = pago?.whatsapp_cobros?.replace(/\D/g, '');
    const waLink = waNum
      ? `https://wa.me/593${waNum.startsWith('0') ? waNum.slice(1) : waNum}?text=${waMensaje}`
      : null;

    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ padding:'2rem 1rem' }}>
            <div style={{ textAlign:'center', marginBottom:'1.5rem' }}>
              <div style={{ fontSize:'3.5rem', marginBottom:'0.5rem' }}>{esPago ? '⏳' : '✅'}</div>
              <h2 style={{ fontSize:'1.4rem', fontWeight:'800', color:'#111827', marginBottom:'0.35rem' }}>
                {esPago ? 'Reserva recibida' : '¡Reserva confirmada!'}
              </h2>
              <p style={{ color:'#6b7280', fontSize:'0.9rem' }}>
                {esPago
                  ? <><strong>Completa el pago</strong> para confirmar tu cita en <strong>{negocio.nombre}</strong></>
                  : <>Hasta pronto en <strong>{negocio.nombre}</strong></>
                }
              </p>
            </div>

            {/* Resumen de cita */}
            <div style={styles.confirmBox}>
              <Row label="Servicio"  value={servicio.nombre} />
              <Row label="Fecha"     value={fmtFechaLarga(fecha)} cap />
              <Row label="Hora"      value={fmtHora(hora)} />
              {profesional && <Row label="Profesional" value={`${profesional.nombres} ${profesional.apellidos}`} />}
              <Row label="Nombre"    value={clienteEncontrado ? `${clienteEncontrado.nombres} ${clienteEncontrado.apellidos}` : `${form.nombre} ${form.apellido}`} />
              <Row label="Cédula"    value={clienteEncontrado ? clienteEncontrado.identificacion : form.cedula} />
              {(clienteEncontrado?.whatsapp_number || form.whatsapp) && <Row label="WhatsApp" value={clienteEncontrado?.whatsapp_number || form.whatsapp} />}
            </div>

            {/* Instrucciones de pago */}
            {esPago && pago && (
              <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'12px', padding:'1.25rem', marginBottom:'1.25rem' }}>
                <p style={{ fontWeight:'800', color:'#92400e', margin:'0 0 0.75rem', fontSize:'1rem' }}>
                  💳 Realiza tu depósito / transferencia
                </p>
                {pago.monto > 0 && (
                  <div style={{ background:'#fff', border:'1px solid #fcd34d', borderRadius:'8px', padding:'0.6rem 1rem', marginBottom:'0.75rem', textAlign:'center' }}>
                    <span style={{ fontSize:'0.8rem', color:'#92400e' }}>Monto a depositar</span>
                    <div style={{ fontSize:'1.75rem', fontWeight:'800', color:'#d97706' }}>${pago.monto.toFixed(2)}</div>
                  </div>
                )}
                <div style={{ display:'grid', gap:'0.4rem', fontSize:'0.875rem', color:'#374151' }}>
                  {pago.banco       && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Banco</span><strong>{pago.banco}</strong></div>}
                  {pago.tipo_cuenta && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Tipo de cuenta</span><strong style={{ textTransform:'capitalize' }}>{pago.tipo_cuenta}</strong></div>}
                  {pago.numero_cuenta && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Número de cuenta</span><strong>{pago.numero_cuenta}</strong></div>}
                  {pago.titular_cuenta && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Titular</span><strong>{pago.titular_cuenta}</strong></div>}
                  {pago.identificacion_titular && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>C.I / RUC</span><strong>{pago.identificacion_titular}</strong></div>}
                </div>
                {pago.instrucciones_adicionales && (
                  <p style={{ margin:'0.75rem 0 0', fontSize:'0.82rem', color:'#78350f', background:'#fef3c7', borderRadius:'6px', padding:'0.5rem 0.75rem' }}>
                    📌 {pago.instrucciones_adicionales}
                  </p>
                )}

                {/* Comprobante adjunto */}
                {numeroComprobante && (
                  <div style={{ marginTop:'0.75rem', padding:'0.65rem 0.85rem', background:'#ecfdf5', border:'1px solid #6ee7b7', borderRadius:'8px', fontSize:'0.85rem', color:'#065f46' }}>
                    ✅ Comprobante N° <strong>{numeroComprobante}</strong> recibido
                  </div>
                )}

                {waLink && (
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', marginTop:'0.75rem', padding:'0.75rem', background:'#25d366', color:'#fff', borderRadius:'10px', fontWeight:'700', fontSize:'0.95rem', textDecoration:'none' }}
                  >
                    <span>📲</span> Notificar por WhatsApp
                  </a>
                )}
              </div>
            )}

            <button style={styles.btnPrimary} onClick={() => window.location.reload()}>
              Hacer otra reserva
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Paso 6: verificando / error PayPhone ─────────────────────────────────
  if (step === 6) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign:'center', padding:'2.5rem 1rem' }}>
          {ppVerificando ? (
            <>
              <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>⏳</div>
              <h2 style={{ fontSize:'1.3rem', fontWeight:'800', color:'#111827' }}>Verificando tu pago...</h2>
              <p style={{ color:'#6b7280', fontSize:'0.9rem' }}>Un momento por favor</p>
            </>
          ) : ppError ? (
            <>
              <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>❌</div>
              <h2 style={{ fontSize:'1.3rem', fontWeight:'800', color:'#dc2626' }}>Pago no completado</h2>
              <p style={{ color:'#6b7280', fontSize:'0.9rem', marginBottom:'1.5rem' }}>{ppError}</p>
              <button style={styles.btnPrimary} onClick={() => window.location.href = window.location.pathname}>
                Volver a intentar
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      {/* Cabecera */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>{negocio.nombre}</h1>
        {negocio.tipo && <p style={styles.headerSub}>{negocio.tipo}</p>}
      </div>

      {/* Progress */}
      <div style={styles.progress}>
        {['Servicio','Profesional','Fecha y hora','Tus datos'].map((label, i) => (
          <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.3rem' }}>
            <div style={{
              ...styles.progressDot,
              background: step > i+1 ? '#10b981' : step === i+1 ? '#3b82f6' : '#e5e7eb',
              color: step >= i+1 ? '#fff' : '#9ca3af',
            }}>
              {step > i+1 ? '✓' : i+1}
            </div>
            <span style={{ fontSize:'0.7rem', color: step === i+1 ? '#3b82f6' : '#9ca3af', fontWeight: step === i+1 ? 700 : 400 }}>
              {label}
            </span>
          </div>
        ))}
      </div>

      <div style={styles.card}>

        {/* ── PASO 1: Servicio ── */}
        {step === 1 && (
          <>
            <h2 style={styles.stepTitle}>¿Qué servicio necesitas?</h2>
            {servicios.length === 0
              ? <p style={styles.empty}>No hay servicios disponibles por el momento.</p>
              : (
                <div style={styles.grid}>
                  {servicios.map(s => (
                    <button
                      key={s.id}
                      style={{ ...styles.serviceCard, ...(servicio?.id === s.id ? styles.serviceCardActive : {}) }}
                      onClick={() => { setServicio(s); setProfesional(null); setFecha(''); setHora(''); setStep(2); }}
                    >
                      <div style={{ fontWeight:'700', fontSize:'1rem', color:'#111827', marginBottom:'0.25rem' }}>{s.nombre}</div>
                      {s.descripcion && <div style={{ fontSize:'0.8rem', color:'#6b7280', marginBottom:'0.5rem' }}>{s.descripcion}</div>}
                      <div style={styles.serviceMeta}>
                        <span>⏱ {fmtDuracion(s.duracion_minutos)}</span>
                        {s.precio > 0 && <span>💲{parseFloat(s.precio).toFixed(2)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )
            }
          </>
        )}

        {/* ── PASO 2: Profesional ── */}
        {step === 2 && (
          <>
            <button style={styles.btnBack} onClick={() => setStep(1)}>← Volver</button>
            <h2 style={styles.stepTitle}>¿Con quién quieres la cita?</h2>
            <div style={styles.grid}>
              {/* Opción "Sin preferencia" */}
              <button
                style={{ ...styles.serviceCard, ...(profesional === null && step === 2 ? {} : {}) }}
                onClick={() => { setProfesional(null); setFecha(''); setHora(''); setStep(3); }}
              >
                <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>🎲</div>
                <div style={{ fontWeight:'700', color:'#111827' }}>Sin preferencia</div>
                <div style={{ fontSize:'0.8rem', color:'#6b7280', marginTop:'0.25rem' }}>El primer disponible</div>
              </button>

              {profesionales.map(p => (
                <button
                  key={p.id}
                  style={styles.serviceCard}
                  onClick={() => { setProfesional(p); setFecha(''); setHora(''); setStep(3); }}
                >
                  <div style={{ ...styles.avatarCircle, background: p.color_calendario || '#3b82f6' }}>
                    {p.nombres[0]}{p.apellidos?.[0] || ''}
                  </div>
                  <div style={{ fontWeight:'700', color:'#111827' }}>{p.nombres} {p.apellidos}</div>
                  {p.especialidad && <div style={{ fontSize:'0.8rem', color:'#6b7280', marginTop:'0.2rem' }}>{p.especialidad}</div>}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── PASO 3: Fecha + hora ── */}
        {step === 3 && (
          <>
            <button style={styles.btnBack} onClick={() => setStep(2)}>← Volver</button>
            <h2 style={styles.stepTitle}>¿Cuándo quieres venir?</h2>

            {/* Calendario */}
            <div style={styles.calWrap}>
              <div style={styles.calHeader}>
                <button style={styles.calNav} onClick={mesAnterior}>‹</button>
                <span style={{ fontWeight:'700', color:'#111827' }}>{MESES[calMes]} {calAnio}</span>
                <button style={styles.calNav} onClick={mesSiguiente}>›</button>
              </div>
              <div style={styles.calGrid}>
                {DIAS.map(d => <div key={d} style={styles.calDayName}>{d}</div>)}
                {diasDelMes().map((d, i) => {
                  if (!d) return <div key={`e${i}`} />;
                  const disponible = esDisponible(d);
                  const seleccionado = d && ymd(d) === fecha;
                  return (
                    <button
                      key={i}
                      disabled={!disponible}
                      onClick={() => { setFecha(ymd(d)); setHora(''); }}
                      style={{
                        ...styles.calDay,
                        ...(seleccionado ? styles.calDaySelected : {}),
                        ...(!disponible ? styles.calDayDisabled : {}),
                        ...(disponible && !seleccionado ? styles.calDayHover : {}),
                      }}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slots */}
            {fecha && (
              <div style={{ marginTop:'1.5rem' }}>
                <p style={{ fontWeight:'600', color:'#374151', marginBottom:'0.75rem' }}>
                  Horarios disponibles — {fmtFechaLarga(fecha)}
                </p>
                {loadSlots ? (
                  <p style={styles.empty}>Buscando horarios...</p>
                ) : diaCerrado ? (
                  <div style={{ textAlign:'center', padding:'1.25rem 0' }}>
                    <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>🚫</div>
                    <p style={{ fontWeight:'700', color:'#dc2626', margin:'0 0 0.25rem' }}>El negocio está cerrado este día</p>
                    <p style={{ color:'#6b7280', fontSize:'0.875rem' }}>Por favor selecciona otra fecha disponible.</p>
                  </div>
                ) : slots.length === 0 ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    {confirmacionEspera ? (
                      <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '1.25rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                        <p style={{ fontWeight: '700', color: '#065f46', margin: '0 0 0.25rem' }}>¡Quedaste en la lista de espera!</p>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Te contactaremos si se libera un cupo para esta fecha.</p>
                      </div>
                    ) : listaEsperaMode ? (
                      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '1.25rem' }}>
                        <p style={{ fontWeight: '700', color: '#92400e', marginTop: 0, marginBottom: '0.5rem' }}>📋 Lista de espera</p>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                          Déjanos tus datos y te avisamos si se libera un cupo para este día.
                        </p>
                        <div className="grid-responsive" style={{ gap: '0.65rem', marginBottom: '0.65rem' }}>
                          <div>
                            <label style={styles.fieldLabel}>Nombre *</label>
                            <input style={styles.input} value={formEspera.nombre} onChange={e => setFormEspera(f => ({...f, nombre: e.target.value}))} placeholder="Tu nombre" />
                          </div>
                          <div>
                            <label style={styles.fieldLabel}>Apellido</label>
                            <input style={styles.input} value={formEspera.apellido} onChange={e => setFormEspera(f => ({...f, apellido: e.target.value}))} placeholder="Tu apellido" />
                          </div>
                          <div>
                            <label style={styles.fieldLabel}>Teléfono *</label>
                            <input style={styles.input} type="tel" value={formEspera.telefono} onChange={e => setFormEspera(f => ({...f, telefono: e.target.value}))} placeholder="0991234567" />
                          </div>
                          <div>
                            <label style={styles.fieldLabel}>Email</label>
                            <input style={styles.input} type="email" value={formEspera.email} onChange={e => setFormEspera(f => ({...f, email: e.target.value}))} placeholder="tu@email.com" />
                          </div>
                        </div>
                        {errorEspera && <div style={styles.errorMsg}>{errorEspera}</div>}
                        <div style={{ display: 'flex', gap: '0.65rem', marginTop: '0.75rem' }}>
                          <button
                            style={{ ...styles.btnPrimary, background: '#f59e0b', flex: 1, opacity: enviandoEspera ? 0.7 : 1 }}
                            onClick={handleSubmitEspera}
                            disabled={enviandoEspera}
                          >
                            {enviandoEspera ? 'Registrando...' : 'Unirme a la lista de espera'}
                          </button>
                          <button
                            style={{ padding: '0.875rem 1rem', background: '#e5e7eb', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', color: '#6b7280' }}
                            onClick={() => { setListaEsperaMode(false); setErrorEspera(''); }}
                          >✕</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                        <p style={styles.empty}>No hay horarios disponibles para este día.</p>
                        <button
                          onClick={() => setListaEsperaMode(true)}
                          style={{ ...styles.btnPrimary, background: '#f59e0b', marginTop: '0.5rem' }}
                        >
                          📋 Unirme a la lista de espera
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={styles.slotsGrid}>
                    {slots.map(s => (
                      <button
                        key={s}
                        onClick={() => setHora(s)}
                        style={{ ...styles.slotBtn, ...(hora === s ? styles.slotBtnActive : {}) }}
                      >
                        {fmtHora(s)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {errorEnvio && (
              <div style={{ ...styles.errorMsg, marginTop:'1rem' }}>{errorEnvio}</div>
            )}

            {hora && (
              <button style={{ ...styles.btnPrimary, marginTop:'1.5rem' }} onClick={() => {
                setErrorEnvio('');
                // Resetear estado de búsqueda al entrar al paso 4
                setCedulaBusqueda('');
                setClienteBuscado(false);
                setClienteEncontrado(null);
                setNumeroComprobante('');
                setComprobanteFile(null);
                setComprobantePreview('');
                setStep(4);
              }}>
                Continuar →
              </button>
            )}
          </>
        )}

        {/* ── PASO 4: Cédula → datos → pago ── */}
        {step === 4 && (
          <>
            {/* Volver */}
            <button
              style={styles.btnBack}
              onClick={() => {
                if (clienteBuscado) {
                  setClienteBuscado(false);
                  setClienteEncontrado(null);
                  setErrorEnvio('');
                } else {
                  setStep(3);
                }
              }}
            >← Volver</button>

            {/* Resumen cita */}
            <div style={styles.resumen}>
              <p style={{ margin:0, fontSize:'0.9rem' }}>
                <strong>{servicio.nombre}</strong> · {fmtFechaLarga(fecha)} · <strong>{fmtHora(hora)}</strong>
                {profesional && <> · {profesional.nombres} {profesional.apellidos}</>}
              </p>
            </div>

            {/* ── Sub-paso A: búsqueda por cédula ── */}
            {!clienteBuscado && (
              <>
                <h2 style={styles.stepTitle}>Ingresa tu número de cédula</h2>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem', marginBottom:'0.75rem' }}>
                  <input
                    type="text"
                    value={cedulaBusqueda}
                    onChange={e => setCedulaBusqueda(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscarCliente()}
                    placeholder="Ej: 1712345678"
                    style={{ ...styles.input, fontSize:'1.1rem', letterSpacing:'0.05em' }}
                    maxLength={13}
                  />
                  <button
                    style={{ ...styles.btnPrimary, margin:0, opacity: buscandoCliente ? 0.7 : 1 }}
                    onClick={buscarCliente}
                    disabled={buscandoCliente || !cedulaBusqueda.trim()}
                  >
                    {buscandoCliente ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
              </>
            )}

            {/* ── Sub-paso B: cliente encontrado ── */}
            {clienteBuscado && clienteEncontrado && (
              <>
                <div style={{ background:'#ecfdf5', border:'1px solid #6ee7b7', borderRadius:'12px', padding:'1rem 1.25rem', marginBottom:'1rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
                  <span style={{ fontSize:'1.75rem' }}>👋</span>
                  <div>
                    <p style={{ margin:0, fontWeight:'700', color:'#065f46', fontSize:'1rem' }}>
                      ¡Hola, {clienteEncontrado.nombres}!
                    </p>
                    <p style={{ margin:0, fontSize:'0.82rem', color:'#6b7280' }}>
                      Cédula: {clienteEncontrado.identificacion}
                      {clienteEncontrado.whatsapp_number && ` · WhatsApp: ${clienteEncontrado.whatsapp_number}`}
                    </p>
                  </div>
                </div>

                <div style={{ marginTop:'0.75rem' }}>
                  <label style={styles.fieldLabel}>Notas adicionales</label>
                  <textarea
                    value={form.notas}
                    onChange={e => setForm(f=>({...f, notas:e.target.value}))}
                    placeholder="¿Algo que debamos saber?"
                    rows={3}
                    style={{ ...styles.input, resize:'vertical' }}
                  />
                </div>
              </>
            )}

            {/* ── Sub-paso B: cliente NO encontrado ── */}
            {clienteBuscado && clienteEncontrado === false && (
              <>
                <h2 style={{ ...styles.stepTitle, marginTop:'0.5rem' }}>Completa tus datos</h2>
                <div className="grid-responsive" style={{ gap: '0.75rem' }}>
                  <Field label="Nombre *"   value={form.nombre}   onChange={v => setForm(f=>({...f, nombre:v}))}   placeholder="Tu nombre" />
                  <Field label="Apellido"   value={form.apellido} onChange={v => setForm(f=>({...f, apellido:v}))} placeholder="Tu apellido" />
                  <Field label="Cédula *"   value={form.cedula}   onChange={v => setForm(f=>({...f, cedula:v}))}   placeholder="Ej: 1712345678" />
                  <Field label="WhatsApp *" value={form.whatsapp} onChange={v => setForm(f=>({...f, whatsapp:v}))} placeholder="Ej: 0991234567" type="tel" />
                  <div style={{ gridColumn:'1 / -1' }}>
                    <Field label="Email"    value={form.email}    onChange={v => setForm(f=>({...f, email:v}))}    placeholder="tu@email.com" type="email" />
                  </div>
                </div>
                <div style={{ marginTop:'0.75rem' }}>
                  <label style={styles.fieldLabel}>Notas adicionales</label>
                  <textarea
                    value={form.notas}
                    onChange={e => setForm(f=>({...f, notas:e.target.value}))}
                    placeholder="¿Algo que debamos saber?"
                    rows={3}
                    style={{ ...styles.input, resize:'vertical' }}
                  />
                </div>
              </>
            )}

            {errorEnvio && <div style={{ ...styles.errorMsg, marginTop:'0.75rem' }}>{errorEnvio}</div>}

            {/* Datos bancarios + comprobante en step 4 */}
            {clienteBuscado && servicio?.requiere_deposito && negocio?.pago?.transferencia && (
              <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:'12px', padding:'1.25rem', marginTop:'1rem' }}>
                <p style={{ fontWeight:'800', color:'#92400e', margin:'0 0 0.75rem', fontSize:'0.95rem' }}>
                  💳 Datos para tu depósito / transferencia
                </p>
                {servicio.monto_deposito > 0 && (
                  <div style={{ background:'#fff', border:'1px solid #fcd34d', borderRadius:'8px', padding:'0.5rem 1rem', marginBottom:'0.75rem', textAlign:'center' }}>
                    <span style={{ fontSize:'0.8rem', color:'#92400e' }}>Monto a depositar</span>
                    <div style={{ fontSize:'1.75rem', fontWeight:'800', color:'#d97706' }}>${parseFloat(servicio.monto_deposito).toFixed(2)}</div>
                  </div>
                )}
                <div style={{ display:'grid', gap:'0.4rem', fontSize:'0.875rem', color:'#374151', marginBottom:'1rem' }}>
                  {negocio.pago.transferencia.banco            && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Banco</span><strong>{negocio.pago.transferencia.banco}</strong></div>}
                  {negocio.pago.transferencia.tipo_cuenta      && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Tipo de cuenta</span><strong style={{ textTransform:'capitalize' }}>{negocio.pago.transferencia.tipo_cuenta}</strong></div>}
                  {negocio.pago.transferencia.numero_cuenta    && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Número de cuenta</span><strong>{negocio.pago.transferencia.numero_cuenta}</strong></div>}
                  {negocio.pago.transferencia.titular_cuenta   && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>Titular</span><strong>{negocio.pago.transferencia.titular_cuenta}</strong></div>}
                  {negocio.pago.transferencia.identificacion_titular && <div style={{ display:'flex', justifyContent:'space-between' }}><span style={{ color:'#6b7280' }}>C.I / RUC</span><strong>{negocio.pago.transferencia.identificacion_titular}</strong></div>}
                </div>
                <div style={{ borderTop:'1px solid #fcd34d', paddingTop:'1rem' }}>
                  <p style={{ fontWeight:'700', color:'#92400e', margin:'0 0 0.75rem', fontSize:'0.875rem' }}>
                    📎 Adjunta tu comprobante de depósito:
                  </p>
                  <div style={{ marginBottom:'0.65rem' }}>
                    <label style={{ display:'block', fontSize:'0.82rem', fontWeight:'600', color:'#374151', marginBottom:'0.35rem' }}>N° de depósito o transferencia</label>
                    <input
                      type="text"
                      value={numeroComprobante}
                      onChange={e => setNumeroComprobante(e.target.value)}
                      placeholder="Ej: 0012345678"
                      style={{ width:'100%', boxSizing:'border-box', padding:'0.6rem 0.75rem', border:'1.5px solid #fcd34d', borderRadius:'8px', fontSize:'0.9rem', background:'#fff' }}
                    />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'0.82rem', fontWeight:'600', color:'#374151', marginBottom:'0.35rem' }}>Imagen del comprobante</label>
                    <input type="file" accept="image/*" onChange={handleComprobanteFile} style={{ display:'none' }} id="comprobante-file-step4" />
                    <label htmlFor="comprobante-file-step4" style={{ display:'block', padding:'0.65rem 1rem', background: comprobanteFile ? '#dcfce7' : '#fff', border:`1.5px dashed ${comprobanteFile ? '#16a34a' : '#fcd34d'}`, borderRadius:'8px', cursor:'pointer', textAlign:'center', color: comprobanteFile ? '#15803d' : '#92400e', fontSize:'0.875rem', fontWeight:'600' }}>
                      {comprobanteFile ? `✅ ${comprobanteFile.name}` : '📎 Seleccionar imagen'}
                    </label>
                    {comprobantePreview && <img src={comprobantePreview} alt="Comprobante" style={{ maxWidth:'100%', maxHeight:'140px', marginTop:'0.5rem', borderRadius:'8px', objectFit:'contain', border:'1px solid #e5e7eb' }} />}
                  </div>
                </div>
              </div>
            )}

            {/* Botones de pago — solo visibles después de buscar */}
            {clienteBuscado && (() => {
              const requiereDeposito = !!servicio?.requiere_deposito;
              const tieneTrans = requiereDeposito && !!negocio?.pago?.transferencia;
              const tienePP    = requiereDeposito && !!negocio?.pago?.payphone;
              const ocupado    = enviando || enviandoPP;

              if (!tieneTrans && !tienePP) {
                return (
                  <button style={{ ...styles.btnPrimary, marginTop:'1.25rem', opacity: ocupado ? 0.7 : 1 }} onClick={handleReservar} disabled={ocupado}>
                    {enviando ? 'Confirmando...' : 'Confirmar reserva'}
                  </button>
                );
              }
              if (tieneTrans && tienePP) {
                return (
                  <div style={{ marginTop:'1.25rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                    <p style={{ margin:0, fontSize:'0.82rem', fontWeight:'600', color:'#374151', textAlign:'center' }}>¿Cómo deseas pagar el anticipo?</p>
                    <button style={{ ...styles.btnPrimary, background:'#f59e0b', opacity: ocupado ? 0.7 : 1 }} onClick={handleReservar} disabled={ocupado}>
                      {enviando ? 'Confirmando...' : '🏦 Depósito / Transferencia'}
                    </button>
                    <button style={{ ...styles.btnPrimary, background:'#7c3aed', opacity: ocupado ? 0.7 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }} onClick={handlePayPhone} disabled={ocupado}>
                      {enviandoPP ? 'Redirigiendo...' : <><span>💳</span> Pagar con tarjeta (PayPhone)</>}
                    </button>
                  </div>
                );
              }
              if (tieneTrans) {
                return (
                  <button style={{ ...styles.btnPrimary, background:'#f59e0b', marginTop:'1.25rem', opacity: ocupado ? 0.7 : 1 }} onClick={handleReservar} disabled={ocupado}>
                    {enviando ? 'Confirmando...' : '🏦 Confirmar reserva'}
                  </button>
                );
              }
              return (
                <button style={{ ...styles.btnPrimary, background:'#7c3aed', marginTop:'1.25rem', opacity: ocupado ? 0.7 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }} onClick={handlePayPhone} disabled={ocupado}>
                  {enviandoPP ? 'Redirigiendo...' : <><span>💳</span> Pagar con tarjeta (PayPhone)</>}
                </button>
              );
            })()}

            {/* ── Modal: cliente no encontrado ── */}
            {modalNuevoCliente && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'1rem' }}>
                <div style={{ background:'#fff', borderRadius:'16px', padding:'1.75rem 1.5rem', maxWidth:'380px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
                  <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
                    <div style={{ fontSize:'2.5rem', marginBottom:'0.5rem' }}>👤</div>
                    <h3 style={{ margin:'0 0 0.5rem', fontSize:'1.1rem', fontWeight:'800', color:'#111827' }}>
                      Cliente no encontrado
                    </h3>
                    <p style={{ margin:0, color:'#6b7280', fontSize:'0.875rem', lineHeight:'1.5' }}>
                      No encontramos un registro con la cédula <strong style={{ color:'#111827' }}>{cedulaBusqueda}</strong>.<br/>
                      Vamos a registrarte como nuevo cliente.
                    </p>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'0.65rem' }}>
                    <button
                      style={{ ...styles.btnPrimary, margin:0 }}
                      onClick={() => { setModalNuevoCliente(false); setClienteBuscado(true); }}
                    >
                      Continuar con el registro
                    </button>
                    <button
                      style={{ padding:'0.75rem', background:'#f3f4f6', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'600', color:'#6b7280', fontSize:'0.9rem' }}
                      onClick={() => { setModalNuevoCliente(false); setClienteEncontrado(null); }}
                    >
                      Volver e intentar otra cédula
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </div>

      <p style={{ textAlign:'center', color:'#d1d5db', fontSize:'0.75rem', marginTop:'1.5rem' }}>
        Powered by FactuTracks
      </p>
    </div>
  );
}

// ── Sub-componentes ──────────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, type='text' }) {
  return (
    <div>
      <label style={styles.fieldLabel}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={styles.input}
      />
    </div>
  );
}

function Row({ label, value, cap }) {
  return (
    <div style={{ display:'flex', gap:'0.5rem', justifyContent:'space-between', padding:'0.45rem 0', borderBottom:'1px solid #f3f4f6' }}>
      <span style={{ color:'#6b7280', fontSize:'0.875rem' }}>{label}</span>
      <span style={{ fontWeight:'600', color:'#111827', fontSize:'0.875rem', textAlign:'right', textTransform: cap ? 'capitalize' : 'none' }}>{value}</span>
    </div>
  );
}

// ── Estilos ──────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
    padding: '1.5rem 1rem 3rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  header: {
    textAlign: 'center',
    marginBottom: '1.25rem',
  },
  headerTitle: {
    fontSize: '1.75rem',
    fontWeight: '800',
    color: '#111827',
    margin: 0,
  },
  headerSub: {
    color: '#6b7280',
    margin: '0.25rem 0 0',
    fontSize: '0.9rem',
    textTransform: 'capitalize',
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    gap: '2.5rem',
    marginBottom: '1.5rem',
  },
  progressDot: {
    width: '2rem',
    height: '2rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: '700',
    transition: 'all 0.2s',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '1.75rem',
    maxWidth: '640px',
    margin: '0 auto',
    boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
  },
  stepTitle: {
    fontSize: '1.15rem',
    fontWeight: '800',
    color: '#111827',
    marginBottom: '1.25rem',
    marginTop: 0,
  },
  btnBack: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '1rem',
    display: 'block',
  },
  btnPrimary: {
    width: '100%',
    padding: '0.875rem',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '0.875rem',
  },
  serviceCard: {
    background: '#f9fafb',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1rem',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
    outline: 'none',
  },
  serviceCardActive: {
    borderColor: '#3b82f6',
    background: '#eff6ff',
  },
  serviceMeta: {
    display: 'flex',
    gap: '0.75rem',
    fontSize: '0.8rem',
    color: '#6b7280',
    fontWeight: '500',
  },
  avatarCircle: {
    width: '3rem',
    height: '3rem',
    borderRadius: '50%',
    color: '#fff',
    fontWeight: '700',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 0.5rem',
  },
  calWrap: {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  calHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  calNav: {
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    width: '2rem',
    height: '2rem',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#374151',
  },
  calGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    padding: '0.5rem',
    gap: '2px',
  },
  calDayName: {
    textAlign: 'center',
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#9ca3af',
    padding: '0.4rem 0',
  },
  calDay: {
    textAlign: 'center',
    padding: '0.5rem',
    borderRadius: '8px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#111827',
    fontWeight: '500',
  },
  calDaySelected: {
    background: '#3b82f6',
    color: '#fff',
    fontWeight: '700',
  },
  calDayDisabled: {
    color: '#d1d5db',
    cursor: 'default',
  },
  calDayHover: {
    cursor: 'pointer',
  },
  slotsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
    gap: '0.5rem',
  },
  slotBtn: {
    padding: '0.6rem',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    background: '#f9fafb',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#374151',
    transition: 'all 0.15s',
  },
  slotBtnActive: {
    background: '#3b82f6',
    color: '#fff',
    borderColor: '#3b82f6',
  },
  resumen: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '10px',
    padding: '0.85rem 1rem',
    marginBottom: '1.25rem',
    color: '#1e40af',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.3rem',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  errorMsg: {
    background: '#fee2e2',
    color: '#dc2626',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    marginTop: '0.75rem',
  },
  confirmBox: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.5rem',
    textAlign: 'left',
  },
  empty: {
    color: '#9ca3af',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '1.5rem 0',
  },
  errorPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f9fafb',
  },
  errorBox: {
    textAlign: 'center',
    padding: '2rem',
  },
  loadingPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '2.5rem',
    height: '2.5rem',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};
